import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { ImportExportRounded, ArrowRightAltRounded, KeyboardArrowDownRounded, KeyboardArrowUpRounded, MoreVert } from "@mui/icons-material";
import { AuthContext } from "../context/AuthContext";
import SearchBarWebex from '../pages/Webex/Statistics/SearchBarWebex';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { convertTimestampToFrenchFormat } from "../scripts/utils";
import FileSaverExcel from './FileSaverExcel';
import { useTranslation } from "react-i18next";

const validateFilters = (parsedData) => {
    if (!Array.isArray(parsedData)) return false;
    return parsedData.every(
        (f) => typeof f.column === 'string' && Array.isArray(f.values)
    );
};

export default function Table({ tableData, tableColumns, toolbarActions, setLink, rowComponent: RowComponent, FileSaverExcelData, FileSaverExcelName, admin, date, sites, referrer, licences, familles, allRoles, holding, state, setState, stateQueryRow, setStateQueryRow, setLinkType, getRowClassName }) {
    const { user } = useContext(AuthContext);
    const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
    const [firstCalls, setFirstCall] = useState([]);

    const sessionKey = `filters_history_${FileSaverExcelName || 'TableData'}`;

    const [filters, setFilters] = useState(() => {
        try {
            const savedFilters = sessionStorage.getItem(sessionKey);
            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);
                if (validateFilters(parsed)) {
                    return parsed;
                }
            }
        } catch (error) {
            console.error(`Erreur de lecture des filtres pour ${sessionKey}`, error);
            sessionStorage.removeItem(sessionKey);
        }
        return [];
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const itemsPerPage = 10;
    const { t } = useTranslation();

    const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => {
            return acc && acc[part] !== undefined ? acc[part] : null;
        }, obj);
    };

    useEffect(() => {
        try {
            sessionStorage.setItem(sessionKey, JSON.stringify(filters));
        } catch (error) {
            console.error(`Erreur d'écriture des filtres pour ${sessionKey}`, error);
        }
    }, [filters, sessionKey]);

    useEffect(() => {
        const data = Array.isArray(tableData) ? tableData : [];

        let filteredData = data;

        if (filteredData.length > 0 && filters.length > 0) {
            filters.forEach(filter => {
                filteredData = filteredData.filter(item =>
                    filter.values.includes(getNestedValue(item, filter.column))
                );
            });
        }

        setFirstCall(filteredData);
        setCurrentPage(1);
    }, [filters, tableData]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.column === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        }
        setSortConfig({ column: key, direction });
    };

    const sortedData = () => {
        let filteredData = firstCalls;
        filters.forEach(filter => {
            filteredData = filteredData.filter(item => filter.values.includes(getNestedValue(item, filter.column)));
        });

        if (sortConfig.column !== null) {
            filteredData = filteredData.sort((a, b) => {
                const aValue = getNestedValue(a, sortConfig.column);
                const bValue = getNestedValue(b, sortConfig.column);

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredData;
    };

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return sortedData().slice(start, end);
    }, [sortedData, currentPage, itemsPerPage]);

    const toggleRowExpansion = useCallback((entryId) => {
        setExpandedRows(prevRows => {
            const newRows = new Set(prevRows);
            newRows.has(entryId) ? newRows.delete(entryId) : newRows.add(entryId);
            return newRows;
        });
    }, []);

    const handleThClick = () => {
        setExpandedRows(prevRows => {
            if (prevRows.size === 0) {
                const allRows = new Set(tableData.map((row, index) => row.id || index));
                return allRows;
            } else {
                return new Set();
            }
        });
    };

    const handlePreviousPage = () => {
        setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
    };

    const totalPages = Math.ceil(sortedData().length / itemsPerPage);

    const currentVisibleData = sortedData();

    const exportData = useMemo(() => {
        if (!FileSaverExcelData || FileSaverExcelData.length === 0) return [];

        if (filters.length === 0) {
            return FileSaverExcelData;
        }

        const is2DArray = Array.isArray(FileSaverExcelData[0]);
        let dataToFilter = is2DArray ? FileSaverExcelData[0] : FileSaverExcelData;

        let filteredExcelData = dataToFilter;

        filters.forEach(filter => {
            filteredExcelData = filteredExcelData.filter(item => {
                const valueToTest = getNestedValue(item, filter.column);
                return valueToTest !== null ? filter.values.includes(valueToTest) : false;
            });
        });

        return is2DArray ? [filteredExcelData] : filteredExcelData;

    }, [FileSaverExcelData, filters]);

    return (
        <div id="table" className="container-listing-statistics">
            <div className='search-bar-webex'>
                <SearchBarWebex dataWebex={firstCalls} dataWebexColumns={tableColumns} filters={filters} setFilters={setFilters} statusAnswered={true} />
                {toolbarActions}
                {FileSaverExcelData && FileSaverExcelName && user && user.roles.includes('ROLE_SUPERADMIN') && (
                    <div className='tooltip-csv-tableWebex'>
                        <MoreVert />
                        <span className='tooltip-csv-tableWebex-button'><FileSaverExcel data={exportData} name={FileSaverExcelName} /></span>
                    </div>
                )}
            </div>
            <section className="statistics-device-container">
                <div className="statistics-table">
                    <div className="statistics-table-wrapper">
                        <table cellPadding="0" cellSpacing="0" border="0">
                            <thead className="statistics-table-wrapper-header">
                                <tr>
                                    {RowComponent && admin ? (
                                        user.roles.includes("ROLE_SUPERADMIN") && (
                                            <th onClick={handleThClick}>
                                                {expandedRows.size > 0 ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                                            </th>
                                        )
                                    ) : RowComponent && (
                                        <th onClick={handleThClick}>
                                            {expandedRows.size > 0 ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                                        </th>
                                    )}
                                    {tableColumns.map((item, index) => (
                                        <React.Fragment key={index}>
                                            {item.headerColumn && (
                                                <th key={index} onClick={() => requestSort(item.accessor)}>
                                                    <div className="statistics-table-wrapper-header-content">
                                                        {t(`${item.Header}`)}
                                                        <div className='statistics-table-wrapper-header-content-container-icons'>
                                                            {sortConfig.column !== item.accessor && (
                                                                <span className='statistics-table-wrapper-header-content-icon'>
                                                                    <ImportExportRounded />
                                                                </span>
                                                            )}
                                                            <span>
                                                                {sortConfig.column === item.accessor && (
                                                                    sortConfig.direction === 'asc' ? (
                                                                        <span className='statistics-table-wrapper-header-content-icon-asc'><ArrowRightAltRounded /></span>
                                                                    ) : sortConfig.direction === 'desc' && (
                                                                        <span className='statistics-table-wrapper-header-content-icon-desc'><ArrowRightAltRounded /></span>
                                                                    )
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </th>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="statistics-table-wrapper-content">
                                {firstCalls.length > 0 ? (
                                    paginatedData.map((data, index) => (
                                        <React.Fragment key={index}>
                                            <tr className={getRowClassName ? getRowClassName(data) : ""}>
                                                {RowComponent && admin ? (
                                                    user.roles.includes("ROLE_SUPERADMIN") && (
                                                        <td className='statistics-table-wrapper-content-element'>
                                                            <div className='legsCallingOpen' onClick={() => toggleRowExpansion(data.id || index)}>
                                                                {expandedRows.has(data.id || index) ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                                                            </div>
                                                        </td>
                                                    )
                                                ) : RowComponent && (
                                                    <td className='statistics-table-wrapper-content-element'>
                                                        <div className='legsCallingOpen' onClick={() => toggleRowExpansion(data.id || index)}>
                                                            {expandedRows.has(data.id || index) ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                                                        </div>
                                                    </td>
                                                )}
                                                {tableColumns.map((item, i) => (
                                                    item.headerColumn && (
                                                        <td key={i} className="statistics-table-wrapper-content-element">
                                                            {item.Cell ? (
                                                                <item.Cell cell={{ value: getNestedValue(data, item.accessor), row: { original: data } }} />
                                                            ) : (
                                                                <div className='container-content-table'>
                                                                    {item.accessor === 'producer' && (
                                                                        <img className='logo_cato_items' src='./assets/cato_logo.svg' alt="logo cato" />
                                                                    )}
                                                                    {item.accessor === 'status' && (
                                                                        data[item.accessor] === "Open" ? (
                                                                            <div className='cato-status-type-status'>
                                                                                <FontAwesomeIcon icon={faCircleCheck} color={'#AEC353'} />
                                                                            </div>
                                                                        ) : data[item.accessor] === "Closed" ? (
                                                                            <div className='cato-status-type-status'>
                                                                                <FontAwesomeIcon icon={faCircleXmark} color={'red'} />
                                                                            </div>
                                                                        ) : data[item.accessor] === "In Progress" ? (
                                                                            <div className='cato-status-type-status'>
                                                                                <img src='./assets/icon-progress.svg' alt="icône de progression" />
                                                                            </div>
                                                                        ) : data[item.accessor] === "Reopened" && (
                                                                            <div className='cato-status-type-status'>
                                                                                <img src='./assets/icon-reload.svg' alt="icône de reload" />
                                                                            </div>
                                                                        )
                                                                    )}
                                                                    {getNestedValue(data, item.accessor) !== null && getNestedValue(data, item.accessor) !== undefined ?
                                                                        <div className={
                                                                            getNestedValue(data, item.accessor) >= 1 && getNestedValue(data, item.accessor) <= 3 ? "criticality_green" :
                                                                                getNestedValue(data, item.accessor) >= 4 && getNestedValue(data, item.accessor) <= 6 ? "criticality_orange" :
                                                                                    getNestedValue(data, item.accessor) >= 7 && getNestedValue(data, item.accessor) <= 10 ? "criticality_red" :
                                                                                        item.accessor === "description" ? "description-width" :
                                                                                            item.accessor === "Référence" && item.link === true || item.accessor === "Contract" && item.link === true ? "id-width" :
                                                                                                item.accessor === "id" && item.link === true ? "id-width" : ""
                                                                        }
                                                                            title={getNestedValue(data, item.accessor)}
                                                                            onClick={() => {
                                                                                if (item.link === true) {
                                                                                    setLink(getNestedValue(data, item.accessor));
                                                                                    if (item.linkType) {
                                                                                        setLinkType(item.linkType);
                                                                                    }
                                                                                }
                                                                            }}
                                                                        >
                                                                            {
                                                                                item.accessor === "createdAt" || item.accessor === "updatedAt" || item.accessor === "expiration" ?
                                                                                    convertTimestampToFrenchFormat(getNestedValue(data, item.accessor), t('en-EN')) :
                                                                                    getNestedValue(data, item.accessor)
                                                                            }
                                                                        </div>
                                                                        : 'NA'}
                                                                </div>
                                                            )}
                                                        </td>
                                                    )
                                                ))}
                                            </tr>
                                            {RowComponent && expandedRows.has(data.id || index) && (
                                                <tr key={`expanded-row-${data.id || index}`} className={getRowClassName ? getRowClassName(data) : ""}>
                                                    <td colSpan={15} className="expanded-details">
                                                        <RowComponent
                                                            item={data}
                                                            date={date}
                                                            sites={sites}
                                                            referrer={referrer}
                                                            licences={licences}
                                                            familles={familles}
                                                            allRoles={allRoles}
                                                            holding={holding}
                                                            setState={setState}
                                                            state={state}
                                                            stateQueryRow={stateQueryRow}
                                                            setStateQueryRow={setStateQueryRow}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={15} className='noAvailableDataCalling'>
                                            {t("No data available")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="pagination-controls-webex-statistics">
                        <button onClick={handlePreviousPage} disabled={currentPage === 1}>
                            <FontAwesomeIcon icon={faChevronLeft} color={'grey'} />
                        </button>
                        <span>Page {currentPage} {t('sur')} {totalPages}</span>
                        <button onClick={handleNextPage} disabled={currentPage === totalPages}>
                            <FontAwesomeIcon icon={faChevronRight} color={'grey'} />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}
